import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset for exploring sine/trigonometric functions.
 *
 * Features:
 * - Sine function with adjustable parameters
 * - Translate handle for moving the function
 * - Extrema (maxima/minima) visualization
 * - Roots visualization
 * - Amplitude, period, and phase shift visualization
 * - Dual x-axes: radians (π) and degrees
 */
export const SINE_PRESET: GraphpadWidgetConfig = {
  id: 'sine',
  type: 'graphpad',
  params: {
    f: {
      type: 'sine',
      params: {
        a: 1,
        b: 1,
        c: 2,
        d: -1,
      },
    },
    'f-translate': {
      type: 'handle',
      params: { of: 'f', behavior: 'translate' },
    },
    'f-extrema': {
      type: 'extrema',
      params: { of: 'f' },
    },
    'f-roots': {
      type: 'roots',
      params: { of: 'f' },
    },
    'f-amplitude': {
      type: 'amplitude',
      params: { of: 'f' },
    },
    'f-period': {
      type: 'period',
      params: { of: 'f' },
    },
    'f-phaseShift': {
      type: 'phaseShift',
      params: { of: 'f' },
    },
  },
  display: {
    coordinateSystem: {
      boundingbox: { xStart: -Math.PI / 2, xEnd: (7 * Math.PI) / 2, yStart: -3.5, yEnd: 2.5 },
      grid: {
        type: 'lines',
        visible: true,
      },
      axes: [
        {
          id: 'x',
          orientation: 'horizontal',
          position: 0,
          isPrimary: true,
          drawGridLines: true,
          ticks: {
            ticksDistance: Math.PI / 2,
            labelFormat: 'pi',
          },
          axisLabel: {
            text: 'θ',
          },
        },
        {
          id: 'x-degrees',
          orientation: 'horizontal',
          position: -3,
          isPrimary: false,
          ticks: {
            ticksDistance: Math.PI / 2,
            labelFormat: 'degrees',
          },
        },
        {
          id: 'y',
          orientation: 'vertical',
          position: 0,
          isPrimary: true,
          drawGridLines: true,
          ticks: {
            ticksDistance: 1,
          },
        },
      ],
    },
    elements: {
      'f-extrema': {
        subparts: {
          maxima: { point: { label: { text: 'max', position: 'topRight' } } },
          minima: { point: { label: { text: 'min', position: 'bottomLeft' } } },
        },
      },
      'f-amplitude': {
        subparts: {
          top: {
            line: {
              label: { text: 'A = {length}', position: 'right', variant: 'secondary' },
            },
          },
        },
      },
      'f-period': {
        subparts: {
          arrow: {
            line: {
              label: { text: 'T = {length}', position: 'top', variant: 'secondary' },
            },
          },
        },
      },
      'f-phaseShift': {
        subparts: {
          arrow: {
            line: {
              label: { text: 'c = {length}', position: 'top', variant: 'secondary' },
            },
          },
        },
      },
    },
  },
};
